import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react"
import { toast } from "sonner"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/lib/auth/auth-context"
import type { Transaction } from "@/lib/types"
import { fetchTransactions, calculateTotals, fetchTransactionsByMonth } from "@/lib/services/transactions"
import type { TransactionTotals, TransactionsByMonth } from "@/lib/services/transactions"

interface TransactionContextType {
  transactions: Transaction[]
  totals: TransactionTotals
  transactionsByMonth: TransactionsByMonth
  loading: boolean
  refreshTransactions: () => Promise<void>
}

const TransactionContext = createContext<TransactionContextType>({
  transactions: [],
  totals: { income: 0, expenses: 0, balance: 0 },
  transactionsByMonth: {},
  loading: true,
  refreshTransactions: async () => {},
})

const DEV = import.meta.env.DEV
const log = (...args: unknown[]) => { if (DEV) console.log(...args) }

// Coalesce realtime-driven refreshes so a burst of inserts (e.g. bulk import,
// or several rapid taps) collapses into one round-trip instead of N.
const REALTIME_DEBOUNCE_MS = 120

export function TransactionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const userId = user?.id
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [totals, setTotals] = useState<TransactionTotals>({ income: 0, expenses: 0, balance: 0 })
  const [transactionsByMonth, setTransactionsByMonth] = useState<TransactionsByMonth>({})
  const [loading, setLoading] = useState(true)
  // Has a fetch ever succeeded? Lets us word the error toast correctly: on the
  // very first load there's no "last data" to fall back to.
  const hasFetchedOnceRef = useRef(false)

  // Fetch all transaction data
  const refreshTransactions = useCallback(async () => {
    log('🔄 [CONTEXT] refreshTransactions called', { userId })

    if (!userId) {
      setTransactions([])
      setTotals({ income: 0, expenses: 0, balance: 0 })
      setTransactionsByMonth({})
      setLoading(false)
      return
    }

    try {
      setLoading(true)

      const [allTransactions, calculatedTotals, groupedTransactions] = await Promise.all([
        fetchTransactions(userId),
        calculateTotals(userId),
        fetchTransactionsByMonth(userId),
      ])

      log('✅ [CONTEXT] Transactions fetched', { count: allTransactions.length })

      setTransactions(allTransactions)
      setTotals(calculatedTotals)
      setTransactionsByMonth(groupedTransactions)
      hasFetchedOnceRef.current = true
    } catch (error) {
      console.error('❌ [CONTEXT] Error refreshing transactions:', error)
      // First-load failure has no prior data to show, so don't claim we're
      // "showing last data" — say it's a load problem instead.
      toast.error(hasFetchedOnceRef.current
        ? 'Could not refresh — showing last data'
        : 'Could not load your data — check your connection')
      // Do not clear state — leave stale data visible rather than wiping the dashboard to $0
    } finally {
      setLoading(false)
    }
  }, [userId])

  // Initial load
  useEffect(() => {
    refreshTransactions()
  }, [refreshTransactions])

  // Supabase Realtime subscriptions
  useEffect(() => {
    if (!userId) return

    log('🔌 [REALTIME] Setting up subscriptions', { userId })

    // Debounced refresh — burst-tolerant.
    let pending: ReturnType<typeof setTimeout> | null = null
    const scheduleRefresh = () => {
      if (pending) clearTimeout(pending)
      pending = setTimeout(() => {
        pending = null
        refreshTransactions()
      }, REALTIME_DEBOUNCE_MS)
    }

    const channel = supabase
      .channel('transaction-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_expenses',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        log('📥 [REALTIME] Expense change', payload.eventType)
        scheduleRefresh()
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_incomes',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        log('📥 [REALTIME] Income change', payload.eventType)
        scheduleRefresh()
      })
      .subscribe((status) => {
        log('🔌 [REALTIME] Status:', status)
        if (String(status) === 'SUBSCRIPTION_ERROR' || String(status) === 'CHANNEL_ERROR') {
          console.error('❌ [REALTIME] Subscription error', status)
        }
      })

    return () => {
      if (pending) clearTimeout(pending)
      log('🔌 [REALTIME] Cleaning up')
      supabase.removeChannel(channel)
    }
  }, [userId, refreshTransactions])

  // Memoize the context value so consumers don't re-render just because the
  // provider itself re-rendered. Without this, every state setter (including
  // unrelated ones higher in the tree) re-creates the value object identity
  // and forces every `useTransactions()` consumer to re-render.
  const value = useMemo<TransactionContextType>(() => ({
    transactions,
    totals,
    transactionsByMonth,
    loading,
    refreshTransactions,
  }), [transactions, totals, transactionsByMonth, loading, refreshTransactions])

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  )
}

export const useTransactions = () => {
  const context = useContext(TransactionContext)
  if (!context) {
    throw new Error("useTransactions must be used within TransactionProvider")
  }
  return context
}
